import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth, useMembers } from "@/context/AuthContext";
import { Field, PageHeader, Row, Spinner } from "@/components/kit";
import OwnershipEditor from "@/components/OwnershipEditor";
import FundingEditor from "@/components/FundingEditor";
import { DOC_TYPES, uploadDoc } from "@/components/Documents";
import { CATEGORY_LABEL, fdate, inr, num, pct, today } from "@/lib/format";

const ALLOC_TYPES = ["buy", "initial_investment", "capital_contribution", "additional_investment", "ownership_change"];
const RECEIPT_TYPES = ["sell", "partial_exit", "complete_exit"];
const BIZ_TYPES = [["capital_contribution", "Capital contribution"], ["additional_investment", "Additional investment"],
  ["ownership_change", "Ownership change"], ["partial_exit", "Partial exit"], ["complete_exit", "Complete exit"]];
const ENTITY_TYPES = ["Private Limited Company", "LLP", "Partnership Firm", "Proprietorship Interest", "Other"];
const COMMODITIES = ["Gold", "Silver", "Copper", "Other"];
const UNITS = ["grams", "kilograms", "units", "lots"];
const STEP_TITLE = { asset: "What are you adding?", details: "Transaction details", ownership: "Who owns it?", funding: "Who paid?", review: "Review & submit" };

function Chips({ options, value, onChange, testid }) {
  return (
    <div className="flex gap-2 flex-wrap" data-testid={testid}>
      {options.map((o) => (
        <button type="button" key={o} data-testid={`${testid}-${o}`} onClick={() => onChange(o)}
          className={`h-10 px-4 rounded-xl text-sm font-semibold border transition-colors ${value === o ? "bg-[#C8553D] text-white border-[#C8553D]" : "bg-white border-[#E2D9CF]"}`}>{o}</button>
      ))}
    </div>
  );
}

function AssetStep({ category, asset, set }) {
  const up = (k) => (e) => set({ ...asset, [k]: e.target.value });
  if (category === "stock")
    return (
      <div className="space-y-4">
        <Field label="Company name"><input data-testid="asset-name-input" className="hd-input" value={asset.name} onChange={up("name")} placeholder="Reliance Industries" /></Field>
        <Field label="Symbol"><input data-testid="asset-symbol-input" className="hd-input uppercase" value={asset.symbol} onChange={up("symbol")} placeholder="RELIANCE" /></Field>
        <Field label="Exchange"><Chips testid="asset-exchange" options={["NSE", "BSE", "Other"]} value={asset.exchange} onChange={(v) => set({ ...asset, exchange: v })} /></Field>
        <div className="text-xs text-[#718096]">Buying a stock you already hold adds a new lot to the same holding.</div>
      </div>
    );
  if (category === "commodity")
    return (
      <div className="space-y-4">
        <Field label="Commodity"><Chips testid="asset-commodity" options={COMMODITIES} value={asset.preset} onChange={(v) => set({ ...asset, preset: v })} /></Field>
        {asset.preset === "Other" && <Field label="Commodity name"><input data-testid="asset-name-input" className="hd-input" value={asset.name} onChange={up("name")} placeholder="Platinum" /></Field>}
        <Field label="Unit"><Chips testid="asset-unit" options={UNITS} value={asset.unit} onChange={(v) => set({ ...asset, unit: v })} /></Field>
      </div>
    );
  return (
    <div className="space-y-4">
      <Field label="Business name"><input data-testid="asset-name-input" className="hd-input" value={asset.name} onChange={up("name")} placeholder="ABC Technologies" /></Field>
      <Field label="Legal entity name"><input data-testid="asset-legal-input" className="hd-input" value={asset.legal_entity_name} onChange={up("legal_entity_name")} placeholder="ABC Technologies Pvt Ltd" /></Field>
      <Field label="Entity type">
        <select data-testid="asset-entity-type-select" className="hd-input" value={asset.entity_type} onChange={up("entity_type")}>{ENTITY_TYPES.map((t) => <option key={t}>{t}</option>)}</select>
      </Field>
      <Field label="Group's ownership / shareholding in the business (%)" hint="Optional — e.g. 25 if the group holds 25% of the company">
        <input data-testid="asset-stake-input" className="hd-input" inputMode="decimal" value={asset.stake_pct} onChange={up("stake_pct")} placeholder="25" />
      </Field>
    </div>
  );
}

function DetailsStep({ category, type, setType, d, set, members, demats, holding, canPickType }) {
  const up = (k) => (e) => set({ ...d, [k]: e.target.value });
  const unit = category === "stock" ? "shares" : holding?.summary?.unit || d._unit;
  const lots = (holding?.summary?.lots || []).filter((l) => l.is_open && (Number(l.available) > 0 || l.id === d.lot_id));
  const executors = members.filter((m) => m.status === "active" || m.person_id === d.executed_by_person_id);
  return (
    <div className="space-y-4">
      {canPickType && (
        <Field label="Transaction type">
          <select data-testid="txn-type-select" className="hd-input" value={type} onChange={(e) => setType(e.target.value)}>{BIZ_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        </Field>
      )}
      {type === "sell" && (
        <Field label="Which lot is being sold?" hint="Choose the acquisition lot — FIFO is not enforced">
          <select data-testid="txn-lot-select" className="hd-input" value={d.lot_id} onChange={up("lot_id")}>
            <option value="">Select a lot…</option>
            {lots.map((l) => <option key={l.id} value={l.id}>Lot #{l.lot_number} · {num(l.quantity)} @ {inr(l.price)} · {num(l.available)} available</option>)}
          </select>
        </Field>
      )}
      <Field label={category === "business" ? "Date" : type === "sell" ? "Sale date" : "Purchase date"}>
        <input data-testid="txn-date-input" type="date" className="hd-input" value={d.date} onChange={up("date")} />
      </Field>
      {category !== "business" ? (
        <>
          <div className="grid grid-cols-2 gap-3">
            <Field label={`Quantity (${unit})`}><input data-testid="txn-quantity-input" className="hd-input" inputMode="decimal" value={d.quantity} onChange={up("quantity")} placeholder="100" /></Field>
            <Field label={category === "stock" ? (type === "sell" ? "Sale price" : "Purchase price") : `Rate per ${unit}`}><input data-testid="txn-price-input" className="hd-input" inputMode="decimal" value={d.price} onChange={up("price")} placeholder="2500" /></Field>
          </div>
          <Field label="Brokerage / charges"><input data-testid="txn-charges-input" className="hd-input" inputMode="decimal" value={d.charges} onChange={up("charges")} /></Field>
          {category === "stock" && (
            <Field label="Demat account (custody)" hint={demats.length ? "Custody does not decide ownership" : "Add a Demat account from Profile first"}>
              <select data-testid="txn-demat-select" className="hd-input" value={d.demat_account_id} onChange={up("demat_account_id")}>
                <option value="">{type === "sell" ? "Same as lot" : "Select Demat account…"}</option>
                {demats.filter((x) => x.status === "active" || x.id === d.demat_account_id).map((x) => <option key={x.id} value={x.id}>{x.account_name} · {x.broker} · {x.holder_name}</option>)}
              </select>
            </Field>
          )}
        </>
      ) : type !== "ownership_change" ? (
        <Field label={RECEIPT_TYPES.includes(type) ? "Proceeds received (₹)" : "Total investment (₹)"}>
          <input data-testid="txn-amount-input" className="hd-input" inputMode="decimal" value={d.amount} onChange={up("amount")} placeholder="400000" />
        </Field>
      ) : null}
      {category === "business" && ["additional_investment", "capital_contribution", "ownership_change", "partial_exit"].includes(type) && (
        <Field label="New shareholding % in the business (optional)" hint={holding?.summary?.stake_pct != null ? `Currently ${pct(holding.summary.stake_pct)}` : ""}>
          <input data-testid="txn-new-stake-input" className="hd-input" inputMode="decimal" value={d.new_stake_pct} onChange={up("new_stake_pct")} />
        </Field>
      )}
      <Field label="Executed / entered by">
        <select data-testid="txn-executed-by-select" className="hd-input" value={d.executed_by_person_id} onChange={up("executed_by_person_id")}>
          {executors.map((m) => <option key={m.person_id} value={m.person_id}>{m.full_name} ({m.hs_id})</option>)}
        </select>
      </Field>
      <Field label="Notes"><textarea data-testid="txn-notes-input" className="hd-input h-24 py-3" value={d.notes} onChange={up("notes")} /></Field>
    </div>
  );
}

function ReviewStep({ category, type, asset, d, total, alloc, fund, members, file, setFile, docType, setDocType, editing, editComment, setEditComment, holding }) {
  const name = (pid) => members.find((m) => m.person_id === pid)?.full_name || pid;
  const assetName = holding?.summary?.asset?.name || (category === "commodity" && asset.preset !== "Other" ? asset.preset : asset.name);
  return (
    <div className="space-y-4">
      <div className="hd-card">
        <Row label="Asset">{assetName}</Row>
        <Row label="Type">{type.replace(/_/g, " ")}</Row>
        <Row label="Date">{fdate(d.date)}</Row>
        {category !== "business" && <Row label="Quantity × price">{num(d.quantity)} × {inr(d.price)}</Row>}
        {category !== "business" && <Row label="Charges">{inr(d.charges || 0)}</Row>}
        <Row label={RECEIPT_TYPES.includes(type) ? "Net proceeds" : "Total value"} testid="review-total">{inr(total)}</Row>
        <Row label="Executed by">{name(d.executed_by_person_id)}</Row>
      </div>
      {Object.keys(alloc).length > 0 && (
        <div className="hd-card"><div className="hd-label mb-1">Ownership</div>
          {Object.entries(alloc).filter(([, v]) => Number(v) > 0).map(([p, v]) => <Row key={p} label={name(p)}>{pct(v)}</Row>)}</div>
      )}
      {Object.keys(fund).length > 0 && (
        <div className="hd-card"><div className="hd-label mb-1">{RECEIPT_TYPES.includes(type) ? "Proceeds received by" : "Paid by"}</div>
          {Object.entries(fund).filter(([, v]) => Number(v) > 0).map(([p, v]) => <Row key={p} label={name(p)}>{inr(v)}</Row>)}</div>
      )}
      {editing && (
        <Field label="Reason for edit"><input data-testid="txn-edit-comment-input" className="hd-input" value={editComment} onChange={(e) => setEditComment(e.target.value)} placeholder="Corrected price as per contract note" /></Field>
      )}
      <div className="hd-card">
        <div className="hd-label mb-2">Supporting document (optional)</div>
        <select data-testid="txn-doc-type-select" className="hd-input h-11 text-sm mb-2" value={docType} onChange={(e) => setDocType(e.target.value)}>{DOC_TYPES.map((t) => <option key={t}>{t}</option>)}</select>
        <input data-testid="txn-doc-file-input" type="file" className="text-sm" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        {file && <div className="text-xs text-[#718096] mt-1">{file.name}</div>}
      </div>
      <div className="text-xs text-[#718096]">After submitting, the other Karta must confirm this entry. It won’t count in balances until confirmed.</div>
    </div>
  );
}

function useWizardData(category, sp, person) {
  const editId = sp.get("edit");
  const [holding, setHolding] = useState(null);
  const [txn, setTxn] = useState(null);
  const [loading, setLoading] = useState(!!(editId || sp.get("holding")));
  useEffect(() => {
    (async () => {
      if (editId) {
        const t = (await api.get(`/transactions/${editId}`)).data;
        setTxn(t);
        setHolding((await api.get(`/holdings/${t.holding_id}`)).data);
      } else if (sp.get("holding")) {
        setHolding((await api.get(`/holdings/${sp.get("holding")}`)).data);
      }
      setLoading(false);
    })().catch((e) => { toast.error(errMsg(e)); setLoading(false); });
  }, [editId]); // eslint-disable-line react-hooks/exhaustive-deps
  return { editId, holding, txn, loading };
}

export default function TxnWizard() {
  const { category } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { gid, person } = useAuth();
  const { kartas, members } = useMembers();
  const { editId, holding, txn, loading } = useWizardData(category, sp, person);
  const [type, setType] = useState(sp.get("type") || (category === "business" ? "initial_investment" : "buy"));
  const [asset, setAsset] = useState({ name: "", symbol: "", exchange: "NSE", unit: "grams", preset: "Gold", legal_entity_name: "", entity_type: ENTITY_TYPES[0], stake_pct: "" });
  const [d, setD] = useState({ date: today(), quantity: "", price: "", charges: "0", amount: "", demat_account_id: "", executed_by_person_id: person.id, lot_id: sp.get("lot") || "", new_stake_pct: "", notes: "" });
  const [alloc, setAlloc] = useState({});
  const [fund, setFund] = useState({});
  const [demats, setDemats] = useState([]);
  const [total, setTotal] = useState("0");
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [docType, setDocType] = useState(DOC_TYPES[0]);
  const [editComment, setEditComment] = useState("");
  const [busy, setBusy] = useState(false);

  const holdingId = holding?.summary?.id;
  useEffect(() => {
    if (category === "stock") api.get(`/groups/${gid}/demat-accounts`).then((r) => setDemats(r.data));
  }, [category, gid]);

  useEffect(() => {
    if (txn) {
      setType(txn.type);
      setD({ date: txn.date, quantity: txn.quantity || "", price: txn.price || "", charges: txn.charges || "0", amount: txn.total_value,
        demat_account_id: txn.demat_account_id || "", executed_by_person_id: txn.executed_by_person_id, lot_id: txn.lot_id || "",
        new_stake_pct: txn.new_stake_pct || "", notes: txn.notes || "" });
      setAlloc(Object.fromEntries(txn.allocations.map((a) => [a.person_id, a.percentage])));
      setFund(Object.fromEntries(txn.funding.map((f) => [f.person_id, f.amount])));
    } else if (holding && category === "business") {
      setAlloc(Object.fromEntries(holding.summary.ownership.persons.map((p) => [p.person_id, p.percentage])));
    } else if (holding && type === "buy" && holding.summary.lots?.length) {
      const last = holding.summary.lots[holding.summary.lots.length - 1];
      setAlloc(Object.fromEntries(last.ownership.persons.map((p) => [p.person_id, p.percentage])));
    }
  }, [txn, holding]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!txn && !Object.keys(alloc).length && kartas.length && !(holding && category === "business")) {
      const each = (100 / kartas.length).toFixed(4);
      setAlloc(Object.fromEntries(kartas.map((k, i) => [k.id, i === kartas.length - 1 ? String(100 - Number(each) * (kartas.length - 1)) : String(Number(each))])));
    }
  }, [kartas]); // eslint-disable-line react-hooks/exhaustive-deps

  const steps = useMemo(() => [
    !holdingId && !editId && "asset", "details", ALLOC_TYPES.includes(type) && "ownership",
    type !== "ownership_change" && "funding", "review",
  ].filter(Boolean), [holdingId, editId, type]);
  const cur = steps[step];
  const receipt = RECEIPT_TYPES.includes(type);

  const previewAlloc = useMemo(() => {
    if (!receipt) return alloc;
    if (type === "sell") {
      const lot = holding?.summary?.lots?.find((l) => l.id === d.lot_id);
      return lot ? Object.fromEntries(lot.ownership.persons.map((p) => [p.person_id, p.percentage])) : {};
    }
    return Object.fromEntries((holding?.summary?.ownership?.persons || []).map((p) => [p.person_id, p.percentage]));
  }, [receipt, type, alloc, holding, d.lot_id]);

  const computeTotal = async () => {
    let t = "0";
    if (category !== "business") {
      t = (await api.post("/calc/total", { kind: type === "sell" ? "sell" : "buy", quantity: d.quantity, price: d.price, charges: d.charges || "0" })).data.total;
    } else if (type !== "ownership_change") {
      t = Number(d.amount || 0).toFixed(2);
    }
    setTotal(t);
    if (!Object.keys(fund).length && Number(t) > 0) setFund({ [d.executed_by_person_id]: t });
    return t;
  };

  const next = async () => {
    try {
      if (cur === "details") await computeTotal();
      setStep((s) => Math.min(s + 1, steps.length - 1));
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        category, type, holding_id: holdingId || null,
        asset: holdingId ? null : { ...asset, name: category === "commodity" && asset.preset !== "Other" ? asset.preset : asset.name },
        ...d, amount: category === "business" ? d.amount || "0" : null,
        allocations: ALLOC_TYPES.includes(type) ? Object.entries(alloc).filter(([, v]) => Number(v) > 0).map(([person_id, percentage]) => ({ person_id, percentage })) : [],
        funding: Object.entries(fund).filter(([, v]) => Number(v) > 0).map(([person_id, amount]) => ({ person_id, amount })),
        edit_comment: editComment,
      };
      const { data } = editId ? await api.put(`/transactions/${editId}`, body) : await api.post(`/groups/${gid}/transactions`, body);
      if (file) {
        try { await uploadDoc("transaction", data.id, file, docType); } catch (e) { toast.error(`Saved, but document upload failed: ${errMsg(e)}`); }
      }
      toast.success(editId ? "Edit submitted for re-confirmation" : "Submitted — awaiting confirmation");
      nav(`/txn/${data.id}`, { replace: true });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading || !kartas.length) return <Spinner />;
  const title = editId ? "Edit transaction" : `${type === "sell" ? "Sell" : "Add"} ${CATEGORY_LABEL[category].replace(/s$/, "").replace("Commoditie", "Commodity")}`;
  return (
    <div className="pb-6">
      <PageHeader title={title} subtitle={holding?.summary?.asset?.name} />
      <div className="mb-5">
        <div className="flex justify-between text-xs font-semibold text-[#718096] mb-1.5"><span data-testid="wizard-step-indicator">Step {step + 1} of {steps.length}</span><span>{STEP_TITLE[cur]}</span></div>
        <div className="h-1.5 rounded-full bg-[#E2D9CF]"><div className="h-full rounded-full bg-[#C8553D] transition-[width] duration-300" style={{ width: `${((step + 1) / steps.length) * 100}%` }} /></div>
      </div>
      <h2 className="text-lg font-bold mb-4">{cur === "funding" && receipt ? "Who received the money?" : cur === "funding" && category === "business" ? "Side contributions — who paid?" : STEP_TITLE[cur]}</h2>
      <div className="fade-up" key={cur}>
        {cur === "asset" && <AssetStep category={category} asset={asset} set={setAsset} />}
        {cur === "details" && <DetailsStep category={category} type={type} setType={setType} d={{ ...d, _unit: asset.unit }} set={setD} members={members} demats={demats} holding={holding} canPickType={category === "business" && holdingId && !editId} />}
        {cur === "ownership" && <OwnershipEditor members={members} kartas={kartas} value={alloc} onChange={setAlloc} />}
        {cur === "funding" && <FundingEditor members={members} kartas={kartas} total={total} value={fund} onChange={setFund} alloc={previewAlloc} receipt={receipt} />}
        {cur === "review" && <ReviewStep {...{ category, type, asset, d, total, alloc: ALLOC_TYPES.includes(type) ? alloc : previewAlloc, fund, members, file, setFile, docType, setDocType, editing: !!editId, editComment, setEditComment, holding }} />}
      </div>
      <div className="flex gap-3 mt-6">
        {step > 0 && <button className="hd-btn-outline flex-1" onClick={() => setStep(step - 1)} data-testid="wizard-back-button">Back</button>}
        {cur !== "review" ? (
          <button className="hd-btn-primary flex-[2]" onClick={next} data-testid="wizard-next-button">Continue</button>
        ) : (
          <button className="hd-btn-primary flex-[2]" onClick={submit} disabled={busy} data-testid="wizard-submit-button">{busy ? "Submitting…" : editId ? "Submit edit" : "Submit for confirmation"}</button>
        )}
      </div>
    </div>
  );
}
